import React, { JSX, useEffect, useMemo, useState } from "react";

import { ZoneId } from "@js-joda/core";
import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import useTimeState from "hooks/dashboard/useTimeState";
import { useTimezone } from "hooks/useTimezone";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import { DweenTimeUnitEnum } from "utils/enums";
import { getAdjustedReferenceTime } from "utils/four-four-five-calendar-utils";
import { getComponentRefTime, toInstant } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import { defaultFromDateTimeInstant, defaultToDateTimeInstant } from "../charts/utils";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";
import DatasetValue from "./DatasetValue";
import DiffRow from "./DiffRow";
import DiffValueForLastValue from "./DiffValueForLastValue";
import DiffValueForMonthYearComparison from "./DiffValueForMonthYearComparison";
import DiffValueForWeekYearComparison from "./DiffValueForWeekYearComparison";
import HorizontalBarChart from "./HorizontalBarChart";
import LastValue from "./LastValue";
import LastValueForMonthYearComparison from "./LastValueForMonthYearComparison";
import LastValueForWeekYearComparison from "./LastValueForWeekYearComparison";
import LastValueTemplateForAverage from "./LastValueTemplateForAverage";
import SumOfValues from "./SumOfValues";
import TitleAndSubtitleTemplate from "./TitleAndSubtitleTemplate";
import "./ValueChangeIndicator.scss";
import { DataDisplayType, DiffValueOptions, ValueChangeIndicatorProps } from "./ValueChangeIndicatorTypes";
import { calculateFromStartOfTheProject, getMainClassName } from "./ValueChangeIndicatorUtils";

export default function ValueChangeIndicator(props: ValueChangeIndicatorProps) {
  const tz = useTimezone();
  const setStatus = useComponentStateCallback(props);

  const [dialog, setDialog] = useState(false);
  const [currentDiffValueOptions, setCurrentDiffValueOptions] = useState<DiffValueOptions>({
    value: 0,
    currentValueInfo: "",
    previousValueInfo: "",
    isHorizontalBarChartDiffLoaded: false,
  });

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const initialReferenceTime = getComponentRefTime(props);

  const refTime = useMemo(
    () =>
      getAdjustedReferenceTime(
        initialReferenceTime,
        tz.company,
        props.calendarType,
        props.useDataUpToLastFinishedMonth,
        props.useDataUpToLastFinishedWeek
      ),
    [
      initialReferenceTime,
      tz.company,
      props.calendarType,
      props.useDataUpToLastFinishedMonth,
      props.useDataUpToLastFinishedWeek,
    ]
  );

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTimeInstant({
      zoneId: tz.company,
      loadConf: { ...defaultLoadConf, ...props.loadConf },
      referenceTime: refTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTimeInstant({
      zoneId: tz.company,
      loadConf: {
        ...defaultLoadConf,
        ...props.loadConf,
        alignUnit: props.useDataUpToLastFinishedMonth ? DweenTimeUnitEnum.MONTHS : props.loadConf?.alignUnit,
        leadUnit: props.useDataUpToLastFinishedMonth ? DweenTimeUnitEnum.MINUTES : props.loadConf?.leadUnit,
        lead: props.useDataUpToLastFinishedMonth ? 0 : props.loadConf?.lead,
      },
      referenceTime: refTime,
    });

  const { data, isLoading, error } = useGetRecentData(props.datasets, {
    count: 2,
    refTime: refTime.toString(),
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled: props.dataDisplayType === DataDisplayType.LAST_VALUE || props.dataDisplayType === undefined,
    },
  });

  const {
    data: dataRange,
    isLoading: isLoadingRange,
    error: errorRange,
  } = useGetDataInfoRange(
    props.datasets,
    {
      timeStart: defaultFromDate.toString(),
      timeEnd:
        props.dataDisplayType === DataDisplayType.SUM_OF_VALUES && props.upToRefTime
          ? refTime.toString()
          : defaultToDate.toString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled:
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ||
        props.dataDisplayType === DataDisplayType.AVERAGE_VALUE ||
        props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON ||
        props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON ||
        props.dataDisplayType === DataDisplayType.SUM_OF_VALUES,
    }
  );

  const decimalPlaces = props.decimalPlaces || data?.[0]?.decimalPlaces;
  const diffDecimalPlaces = props.diffValueDecimalPlaces ?? decimalPlaces;
  const currentDataset = data?.[0] ?? dataRange?.[0];
  const currentData = currentDataset?.data;

  useEffect(() => {
    if (
      data &&
      currentData &&
      currentData.length >= 2 &&
      props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART
    ) {
      const newDiff: DiffValueOptions = {
        value: (currentData[1]?.value ?? 0) - (currentData[0]?.value ?? 0),
        currentValueInfo: renderDiffRow(
          "Current",
          currentData[1]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[1]?.key ?? "",
          tz.company
        ),
        previousValueInfo: renderDiffRow(
          "Previous",
          currentData[0]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[0]?.key ?? "",
          tz.company
        ),
      };
      setCurrentDiffValueOptions(newDiff);
    }
  }, [data, currentData]);

  useEffect(() => {
    if (
      props.reportMode &&
      ((!isLoading && data !== undefined) || (!isLoadingRange && dataRange !== undefined))
    ) {
      if (
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        !currentDiffValueOptions.isHorizontalBarChartDiffLoaded
      ) {
        return undefined;
      }
      return setStatus({ state: "finished" });
    }

    return undefined;
  }, [isLoading, isLoadingRange, currentDiffValueOptions]);

  if (error || errorRange) {
    const message = error?.message ?? errorRange?.message;
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: message,
      severity: "error",
    });
    setStatus({ state: "error", error: message });
    return null;
  }

  const renderDiffRow = (
    label: string,
    value: number,
    unit: string,
    timestamp: string,
    currentTz: ZoneId
  ): JSX.Element => (
    <DiffRow
      label={label}
      value={value}
      unit={unit}
      timestamp={timestamp}
      diffDecimalPlaces={diffDecimalPlaces}
      tz={currentTz}
    />
  );

  const getComponentWidth = () => {
    if (props.reportMode) {
      return `${props.width}px` || "100%";
    }
    return "100%";
  };

  const titleAndSubtitleTemplate = () => (
    <TitleAndSubtitleTemplate
      {...props}
      refTime={refTime}
      calculateFromStartOfTheProject={calculateFromStartOfTheProject(
        tz.company,
        refTime,
        toInstant(defaultFromDate),
        props.startOfTheProject,
        props.useStartOfTheProject
      )}
    />
  );

  const diffValueForWeekYearComparison = () => (
    <DiffValueForWeekYearComparison
      {...props}
      currentDiffValueOptions={currentDiffValueOptions}
      refTime={refTime}
      dataRange={dataRange}
    />
  );

  const horizontalBarChartTemplate = () => {
    if (!dataRange || !props.aggregationPeriod) return null;
    return (
      <div onDoubleClick={() => setDialog(true)}>
        <HorizontalBarChart
          data={dataRange[0]}
          diffType={props.diffType}
          setCurrentDiffValueOptions={setCurrentDiffValueOptions}
          theme={props.theme}
          reportMode={props.reportMode}
          decimalPlaces={decimalPlaces}
          calculateFromStartOfTheProject
          startOfTheProject={props.startOfTheProject}
          useWeekYearComparison={props.dataDisplayType === DataDisplayType.WEEK_YEAR_COMPARISON}
          refTime={refTime}
        />
      </div>
    );
  };

  const diffValueForLastValue = () => (
    <DiffValueForLastValue
      {...props}
      currentDiffValueOptions={currentDiffValueOptions}
      refTime={refTime}
      data={data}
      diffDecimalPlaces={diffDecimalPlaces}
      dataDisplayType={props.dataDisplayType}
      currentData={currentData}
      currentDataset={currentDataset}
    />
  );

  const diffValueForMonthYearComparison = () => (
    <DiffValueForMonthYearComparison
      {...props}
      dataRange={dataRange}
      refTime={refTime}
      currentDiffValueOptions={currentDiffValueOptions}
      currentDataset={currentDataset}
      diffDecimalPlaces={diffDecimalPlaces}
    />
  );

  const lastValueTemplate = () => (
    <LastValue
      {...props}
      data={data}
      setDialog={setDialog}
      refTime={refTime}
      diffDecimalPlaces={diffDecimalPlaces}
    />
  );

  const lastValueTemplateForMonthYearComparison = () => (
    <LastValueForMonthYearComparison
      {...props}
      dataRange={dataRange}
      refTime={refTime}
      diffDecimalPlaces={diffDecimalPlaces}
      setDialog={setDialog}
    />
  );

  const lastValueTemplateForAverage = () => (
    <LastValueTemplateForAverage {...props} dataRange={dataRange} setDialog={setDialog} />
  );

  const lastValueTemplateForWeekYearComparison = () => (
    <LastValueForWeekYearComparison
      {...props}
      dataRange={dataRange}
      refTime={refTime}
      setCurrentDiffValueOptions={setCurrentDiffValueOptions}
      setDialog={setDialog}
    />
  );

  const sumOfValuesTemplate = () => (
    <SumOfValues
      {...props}
      dataRange={dataRange}
      refTime={refTime}
      diffDecimalPlaces={diffDecimalPlaces}
      setDialog={setDialog}
    />
  );

  const flexDirectionClass = props.displayInline ? "flex-row justify-content-between" : "flex-column";

  const className = `${getMainClassName(props.dataDisplayType, props.reportMode, props.displayInline)} flex ${flexDirectionClass}`;
  return (
    <div style={{ width: getComponentWidth() }} className={className}>
      <div className="flex flex-row justify-content-between">
        {titleAndSubtitleTemplate()}
        {diffValueForLastValue()}
        {diffValueForMonthYearComparison()}
        {diffValueForWeekYearComparison()}
      </div>

      {sumOfValuesTemplate()}
      {lastValueTemplate()}
      {lastValueTemplateForMonthYearComparison()}
      {lastValueTemplateForAverage()}
      {lastValueTemplateForWeekYearComparison()}

      {props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        dataRange &&
        props.aggregationPeriod &&
        horizontalBarChartTemplate()}

      {data &&
        data?.length > 1 &&
        !props.displayLastValueDiff &&
        props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART &&
        props.dataDisplayType !== DataDisplayType.AVERAGE_VALUE && (
          <div className={`value-column ${props.displayInline ? "inline-values" : "column-values mt-2"} `}>
            {props.hideIfValueIsZeroOrMore && data[0].data[0].value && data[0].data[0].value >= 0
              ? null
              : data.map((dataset: DatasetDataInfoResponse) => (
                  <DatasetValue
                    key={dataset.identifier}
                    {...props}
                    dataset={dataset}
                    setDialog={setDialog}
                    refTime={refTime}
                    skipMonthCheck={props.skipMonthCheck}
                    diffDecimalPlaces={diffDecimalPlaces}
                  />
                ))}
          </div>
        )}

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => setDialog(false)}
      >
        <DatasetLineChart
          title=""
          identifiers={props.datasets}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </div>
  );
}
